// Product options. The string values are the Shopify variant option values, so they
// double as the lookup key for variants and as what v1 orders stored.

export const SIZES = ['8x12 (20x30cm)', '12x16 (30x40cm)', '16x20 (40x50cm)'] as const;
export const FRAMINGS = ['Unframed', 'Framed to edges', 'Framed with white mount'] as const;
export const FRAME_COLOURS = ['Black', 'White', 'Natural Wood', 'Antique Gold', 'Antique Silver'] as const;

export type Size = (typeof SIZES)[number];
export type Framing = (typeof FRAMINGS)[number];
export type FrameColour = (typeof FRAME_COLOURS)[number] | 'N/A';

export interface Product {
  size: Size;
  framing: Framing;
  frameColour: FrameColour;
}

export const DEFAULT_PRODUCT: Product = { size: '8x12 (20x30cm)', framing: 'Unframed', frameColour: 'N/A' };

export const SIZE_LABELS: Record<Size, { short: string; inches: string; cm: string }> = {
  '8x12 (20x30cm)': { short: '8×12', inches: '8 × 12 in', cm: '20 × 30 cm' },
  '12x16 (30x40cm)': { short: '12×16', inches: '12 × 16 in', cm: '30 × 40 cm' },
  '16x20 (40x50cm)': { short: '16×20', inches: '16 × 20 in', cm: '40 × 50 cm' },
};

export const FRAME_COLOUR_SWATCH: Record<Exclude<FrameColour, 'N/A'>, string> = {
  Black: '#141414',
  White: '#F4F3EF',
  'Natural Wood': '#C8A77A',
  'Antique Gold': '#A8894B',
  'Antique Silver': '#A9A9A6',
};

/**
 * Print file pixel dimensions per size and framing (300 dpi), carried over unchanged
 * from the v1 builder and admin. Framed prints carry extra image for the frame rebate / mount.
 */
const PRINT_PX: Record<Size, Partial<Record<Framing, { width: number; height: number }>>> = {
  '8x12 (20x30cm)': {
    Unframed: { width: 2400, height: 3600 },
    'Framed to edges': { width: 2475, height: 3675 },
    'Framed with white mount': { width: 2925, height: 4125 },
  },
  '12x16 (30x40cm)': {
    Unframed: { width: 3600, height: 4800 },
    'Framed to edges': { width: 3675, height: 4875 },
    'Framed with white mount': { width: 3675, height: 4875 },
  },
  '16x20 (40x50cm)': {
    Unframed: { width: 4800, height: 6000 },
    'Framed to edges': { width: 4875, height: 6075 },
  },
};

export const isAvailable = (size: Size, framing: Framing) => Boolean(PRINT_PX[size]?.[framing]);

export const printPixels = (p: Pick<Product, 'size' | 'framing'>) =>
  PRINT_PX[p.size]?.[p.framing] ?? PRINT_PX[p.size].Unframed!;

/** Fallback prices (GBP), used only until the live Shopify variants have loaded. */
const FALLBACK_PRICE: Record<Size, Partial<Record<Framing, number>>> = {
  '8x12 (20x30cm)': { Unframed: 29.95, 'Framed to edges': 68.95, 'Framed with white mount': 74.95 },
  '12x16 (30x40cm)': { Unframed: 34.95, 'Framed to edges': 74.95, 'Framed with white mount': 76.95 },
  '16x20 (40x50cm)': { Unframed: 39.95, 'Framed to edges': 79.95 },
};

export const fallbackPrice = (p: Pick<Product, 'size' | 'framing'>) => FALLBACK_PRICE[p.size]?.[p.framing] ?? null;

/** Make a product selection valid: no unavailable combination, colour only when framed. */
export function normaliseProduct(p: Product): Product {
  const size = SIZES.includes(p.size) ? p.size : DEFAULT_PRODUCT.size;
  let framing = FRAMINGS.includes(p.framing) ? p.framing : 'Unframed';
  if (!isAvailable(size, framing)) framing = 'Framed to edges';
  let frameColour: FrameColour = p.frameColour;
  if (framing === 'Unframed') frameColour = 'N/A';
  else if (frameColour === 'N/A' || !FRAME_COLOURS.includes(frameColour)) frameColour = 'Black';
  return { size, framing, frameColour };
}
